import { useContext } from 'react'
import classNames from 'classnames'
import { defineMessages, useIntl } from 'react-intl'
import { AmplitudeContext } from '@homebot/shared-util-quartermaster'
import { Text } from '@homebot/shared-ui-swashbuckle'
import { useSelector, useDispatch } from 'react-redux'
import { sendEstimatedPMIRemovalInquiry } from '../../../../store/slices/pmi'
import { selectSponsorshipFirstName } from '../../../../store/selectors/sponsorship'
import { selectCustomerFirstName } from '../../../../store/selectors/customerProfile'
import { OutreachButton } from '../../../'
import { PENDING, SUCCEEDED, FAILED } from '../../../../types/loadingStatus'
import { selectEstimatedPMIInquiryStatus } from '../../../../store/slices/pmi'
import { QUESTIONS, OUTREACH_BUTTON } from '../../../../constants/messages'
import { Button } from '@homebot/shared-ui-swashbuckle'
import { openLoans } from '../../../../actions/homeowner'
import { PmiStepEnum } from '../PmiEstimatedModule'
import { selectCurrentCELs } from '../../../../store/selectors/home/loans'

import styles from './PmiEstimatedDmStep.module.scss'

export interface PmiEstimatedDmStepProps {
  setStep: (step: PmiStepEnum) => void
}

const MSG = defineMessages({
  successMessageTitle: {
    id: 'Pmi.success.message.title',
    defaultMessage: 'Keep an eye on your inbox',
  },
  successMessageSubtitleCustomer: {
    id: 'Pmi.Estimated.success.message.subtitle.customer',
    defaultMessage: '{customer} will check to see if you have PMI.',
  },
  successMessageSubtitleSponsor: {
    id: 'Pmi.Estimated.success.message.subtitle.sponsor',
    defaultMessage:
      '{sponsor} is a lender {customer} trusts, and will check to see if you have PMI.',
  },
  askAboutPMI: {
    id: 'PMI.estimated.askAboutPMI',
    defaultMessage: 'Ask if you have PMI',
  },
  nevermind: {
    id: 'PMI.estimated.nevermind',
    defaultMessage: 'Never mind',
  },
  viewCurrentMortgages: {
    id: 'PMI.estimated.view.current.mortgages',
    defaultMessage:
      'View your {currentLoans, plural, one {mortgage} other {mortgages}}',
  },
})

export const PmiEstimatedDmStep = ({ setStep }: PmiEstimatedDmStepProps) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { ampli } = useContext(AmplitudeContext)

  const sponsorFirstName = useSelector(selectSponsorshipFirstName)
  const customerFirstName = useSelector(selectCustomerFirstName)

  const firstName = sponsorFirstName ? sponsorFirstName : customerFirstName

  const requestStatus = useSelector(selectEstimatedPMIInquiryStatus)

  const currentLoans = useSelector(selectCurrentCELs)

  const handleDMSubmit = () => {
    // TODO: ampli event to track this click?

    const message = intl.formatMessage(
      QUESTIONS.mortgageInsuranceEstimatedPanelFooter
    )
    const topic = intl.formatMessage(QUESTIONS.mortgageInsuranceTopic)

    dispatch(sendEstimatedPMIRemovalInquiry({ message, topic, ampli }))
  }

  const handleNevermindClick = () => {
    setStep(PmiStepEnum.InitialStep)
  }

  const showDMButton = requestStatus === SUCCEEDED || requestStatus === FAILED
  const transformText = requestStatus === SUCCEEDED || requestStatus === FAILED

  const successMessageSubtitle = sponsorFirstName
    ? intl.formatMessage(MSG.successMessageSubtitleSponsor, {
        sponsor: firstName,
        customer: customerFirstName,
      })
    : intl.formatMessage(MSG.successMessageSubtitleCustomer, {
        customer: firstName,
      })

  const handleViewCurrentMortgageClick = () => {
    dispatch(openLoans())
  }

  return (
    <>
      <div className={styles.btnContainer}>
        <OutreachButton
          className={classNames(
            styles.outreachButton,
            transformText && styles.transform
          )}
          buttonMessage={MSG.askAboutPMI}
          requested={requestStatus === PENDING}
          success={requestStatus === SUCCEEDED}
          failure={requestStatus === FAILED}
          colorScheme='btn-brand'
          resultMessage={
            requestStatus === SUCCEEDED
              ? intl.formatMessage(OUTREACH_BUTTON.success)
              : intl.formatMessage(OUTREACH_BUTTON.failure)
          }
          handleClick={handleDMSubmit}
        />
        {!showDMButton && (
          <Button
            type='button'
            colorScheme='btn-secondary'
            onClick={handleNevermindClick}
          >
            {intl.formatMessage(MSG.nevermind)}
          </Button>
        )}
      </div>
      {requestStatus === SUCCEEDED && (
        <>
          <div className={styles.margin}>
            <Text as='b' color='text.heading'>
              {intl.formatMessage(MSG.successMessageTitle)}
            </Text>
          </div>
          <Text marginBottom='spacing-400' size='sm'>
            {successMessageSubtitle}
          </Text>
          <Button
            type='button'
            width={['100%', null, 'inherit']}
            colorScheme='btn-secondary'
            onClick={handleViewCurrentMortgageClick}
          >
            {intl.formatMessage(MSG.viewCurrentMortgages, {
              currentLoans: currentLoans.length,
            })}
          </Button>
        </>
      )}
    </>
  )
}
